import { useState } from "react";
import { api, formatError, fileUrl } from "@/api";
import { Btn, Card, StatusBadge, Tag, TextArea } from "@/components/ui-kit";
import { DetailRow } from "@/components/dashboard-bits";
import { AffiliateBadge } from "@/components/AffiliateCard";
import { Percent, Calendar, Globe, ShoppingBag, Users, Radio, ArrowUpRight, FileText, Info } from "lucide-react";
import { toast } from "sonner";
import { safeUrl, affiliateValidity, formatCommission, COMMISSION_HELP, affiliateTheme, joinType } from "@/lib/affiliate";

function TextBlock({ title, text, testid }) {
  if (!text) return null;
  return (
    <div className="mt-8" data-testid={testid}>
      <h3 className="text-[15px] font-semibold text-[#292827]">{title}</h3>
      <p className="mt-2 text-[15px] text-[#3A3733] leading-7 whitespace-pre-line">{text}</p>
    </div>
  );
}

function JoinCard({ c, applied, setApplied }) {
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(c.my_application_status || null);
  const closed = c.status !== "open" || c.is_expired;
  const joinHref = safeUrl(c.join_link);
  const t = affiliateTheme(c);
  const type = joinType(c);
  const btn = `w-full !py-4 !text-[16px] !rounded-2xl !font-semibold ${t.button}`;

  const apply = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/applications", { campaign_id: c.campaign_id, message: type === "direct" ? "" : message });
      setStatus(data.status);
      setApplied(true);
      toast.success(type === "direct" ? "Olet liittynyt ohjelmaan!" : "Hakemus lähetetty!");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (closed) return <p className="text-[15px] text-[#6E675C]" data-testid="affiliate-closed">Tämä ohjelma ei ole tällä hetkellä avoinna.</p>;

  if (type === "external") return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold tracking-tightish text-[#292827]">Liity ohjelmaan</h2>
      <p className="text-sm text-[#6E675C] leading-relaxed">Liittyminen tapahtuu yrityksen omassa affiliate-järjestelmässä. Creatori ei käsittele liittymistä, myyntiseurantaa tai provisioiden maksamista.</p>
      {joinHref && (
        <Btn as="a" href={joinHref} target="_blank" rel="noopener noreferrer nofollow" variant="bare" className={btn} data-testid="affiliate-join-external">
          Liity yrityksen affiliate-ohjelmaan <ArrowUpRight size={17} />
        </Btn>
      )}
    </div>
  );

  if (applied) return (
    <div className="flex items-center gap-2 text-[#6E675C] text-[15px]" data-testid="affiliate-already-applied">
      <FileText size={18} strokeWidth={1.75} />
      {type === "direct" ? "Olet liittynyt tähän ohjelmaan. Löydät sen Hakemukset-osiosta." : status === "accepted" ? "Hakemuksesi on hyväksytty. Löydät ohjelman Hakemukset-osiosta." : "Olet jo hakenut tähän ohjelmaan. Seuraa hakemuksen tilaa Hakemukset-osiossa."}
    </div>
  );

  if (type === "direct") return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold tracking-tightish text-[#292827]">Liity ohjelmaan</h2>
      <p className="text-sm text-[#6E675C]">Kaikki voivat liittyä – pääset mukaan heti ilman hakemusta tai yrityksen hyväksyntää.</p>
      <Btn variant="bare" className={btn} onClick={apply} disabled={loading} data-testid="affiliate-direct-join-btn">
        {loading ? "Liitytään..." : "Liity ohjelmaan"}
      </Btn>
    </div>
  );

  return (
    <div className="space-y-5">
      <h2 className="text-xl font-semibold tracking-tightish text-[#292827]">Hae mukaan</h2>
      <p className="text-sm text-[#6E675C]">Yritys valitsee mukaan otettavat sisällöntuottajat hakemusten perusteella. Hakemus käsitellään yrityksen Hakemukset-osiossa.</p>
      <TextArea label="Lyhyt hakemusteksti" placeholder="Kerro lyhyesti itsestäsi ja miten markkinoisit tuotteita..."
        value={message} onChange={(e) => setMessage(e.target.value)} data-testid="affiliate-application-message" />
      <Btn variant="bare" className={btn} onClick={apply} disabled={loading || !message.trim()} data-testid="affiliate-apply-btn">
        {loading ? "Lähetetään..." : "Hae mukaan"}
      </Btn>
    </div>
  );
}

export function AffiliateDetail({ c, applied, setApplied }) {
  const website = safeUrl(c.website);
  const shop = safeUrl(c.shop_url);
  const t = affiliateTheme(c);
  return (
    <>
      <Card className={`p-8 md:p-10 ${t.card}`} data-testid="affiliate-detail-card">
        <div className="flex items-center gap-3 flex-wrap">
          <AffiliateBadge c={c} />
          <span className="text-[13px] text-[#968F85]" data-testid="campaign-detail-business">{c.business_name}</span>
        </div>
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-display text-[#292827]" data-testid="campaign-detail-title">{c.title}</h1>
          <StatusBadge status={c.is_expired ? "expired" : c.status} />
        </div>

        <div className={`mt-6 rounded-2xl p-5 ${t.strip}`} data-testid="affiliate-detail-commission">
          <div className={`flex items-center gap-2 ${t.accentText}`}><span className={`h-7 w-7 rounded-full flex items-center justify-center ${t.icon}`}><Percent size={15} strokeWidth={3} /></span><span className="text-3xl font-semibold tracking-display">{formatCommission(c.commission)}</span><span className="text-[15px] text-[#3A3733]">provisio</span></div>
          <p className="mt-2 text-[13px] text-[#3A3733] leading-relaxed flex gap-2"><Info size={14} className="shrink-0 mt-0.5" /> {COMMISSION_HELP}</p>
        </div>

        {c.images?.length > 0 && (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="affiliate-detail-images">
            {c.images.map((src, i) => (
              <div key={i} className="aspect-[4/5] rounded-2xl overflow-hidden bg-[#E9EAEC]"><img src={fileUrl(src)} alt="" loading="lazy" className="h-full w-full object-cover" /></div>
            ))}
          </div>
        )}

        {c.categories?.length > 0 && <div className="mt-6 flex flex-wrap gap-2">{c.categories.map((t) => <Tag key={t}>{t}</Tag>)}</div>}
        <p className="mt-6 text-[15px] text-[#3A3733] leading-7 whitespace-pre-line" data-testid="campaign-detail-description">{c.description}</p>

        <TextBlock title="Tuotteet" text={c.products} testid="affiliate-detail-products" />

        <div className="mt-8 pt-8 border-t border-[#E6E1D9] grid sm:grid-cols-2 gap-6">
          <DetailRow dark icon={Calendar} label="Voimassaolo" value={affiliateValidity(c)} />
          <DetailRow dark icon={Users} label="Kuka voi liittyä" value={{ external: "Yrityksen omien ehtojen mukaan", direct: "Kaikki voivat liittyä", apply: "Yritys valitsee hakijat" }[joinType(c)]} />
          <DetailRow dark icon={Radio} label="Sallitut kanavat" value={(c.channels || []).join(", ") || "—"} />
          <DetailRow dark icon={Globe} label="Kielet" value={(c.languages || []).join(", ") || "—"} />
          <DetailRow dark icon={ShoppingBag} label="Liittymistapa" value={{ external: "Yrityksen oma affiliate-ohjelma", direct: "Suora liittyminen Creatorin kautta", apply: "Hakemus Creatorin kautta" }[joinType(c)]} />
        </div>

        <TextBlock title="Erityisehdot" text={c.special_terms} testid="affiliate-detail-terms" />
        <TextBlock title="Lisätiedot" text={c.additional_info} testid="affiliate-detail-info" />

        {(shop || website) && (
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3">
            {shop && <a href={shop} target="_blank" rel="noopener noreferrer" data-testid="affiliate-detail-shop" className="inline-flex items-center gap-1.5 text-[15px] font-medium text-[#5B7FB0] hover:text-[#292827] transition-colors">Verkkokauppa <ArrowUpRight size={16} /></a>}
            {website && <a href={website} target="_blank" rel="noopener noreferrer" data-testid="campaign-detail-website" className="inline-flex items-center gap-1.5 text-[15px] font-medium text-[#5B7FB0] hover:text-[#292827] transition-colors">Yrityksen verkkosivut <ArrowUpRight size={16} /></a>}
          </div>
        )}
      </Card>
      <Card className="mt-8 p-8" data-testid="affiliate-join-card">
        <JoinCard c={c} applied={applied} setApplied={setApplied} />
      </Card>
    </>
  );
}
